import { Grid, Link as MaterialLink } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { ProductConfigurationResponse } from '../../api/ConfigApi';
import ProductApi from '../../api/ProductApi';
import { AuthData } from '../../context/auth';
import { PolicyData } from '../../context/policy';
import {
	PremiumReportData,
	withPremiumReportContext,
} from '../../context/premiumReport';
import { isEmpty } from '../../util/util';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../common';
import { FastForm } from '../fastForm';
import { FORM_NAME } from '../fastForm/data/constants';
import { getDefaultPolicyValues } from '../fastForm/formBuilders';
import DefaultDatePicker from '../form/DefaultDatePicker';
import LargeCenterSpinner from '../LargeCenterSpinner';
import { errorToast } from '../Toast';
import {
	getPremiumReportStartDate,
	getReportEndDateError,
	getTodaysDateWithTimezone,
	getUpdateReqFromPremiumReportResponse,
} from './util';

type ReportDateError = {
	isValid: boolean;
	message?: string;
};

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	premiumReportData: PremiumReportData;
	fetchPremiumReport?(): void;
}

const ReportDetails = (props: Props) => {
	const { reset, getValues } = useFormContext();
	const { authData, premiumReportData, policyData } = props;
	const { hasPermission } = authData;
	const {
		premiumReport,
		premiumReports,
		premiumReportUpdateReq,
		replaceMode,
		setPremiumReportUpdateReq,
		setReplaceMode,
		setLoading,
		updateFields,
		fields,
	} = premiumReportData;
	const { state } = premiumReport;
	const { policy, productConfig } = policyData;
	const premiumReportingConfigurations =
		productConfig.premiumReportingConfiguration;
	const premiumReportingConfiguration = premiumReportingConfigurations.find(
		(c) => c.reportName === policy.premiumReportName,
	);

	/**
	 * If timestamp is 10 digits, it is in seconds and should be converted to milliseconds.
	 * We're keeping this logic until Rahul is able to run a database script to convert all prior invoiceDueDate timestamps to milliseconds.
	 * https://socotra.atlassian.net/browse/SOCA-7562
	 * @param {string} timestamp
	 * @return {Date}
	 */
	const getInvoiceDueDate = (timestamp: string) => {
		let invoiceDueDate = null;
		if (timestamp?.length === 10) {
			invoiceDueDate = new Date(parseInt(timestamp, 10) * 1000);
		} else {
			invoiceDueDate = new Date(parseInt(timestamp, 10));
		}
		return invoiceDueDate;
	};

	const todaysDateWithTZ = getTodaysDateWithTimezone(authData.auth.timezone);

	const reportStartDate = new Date(
		parseInt(
			getPremiumReportStartDate(premiumReport, premiumReports, policy),
			10,
		),
	);
	const reportEndDate = premiumReportUpdateReq.endTimestamp
		? new Date(parseInt(premiumReportUpdateReq.endTimestamp, 10))
		: null;
	const reportInvoiceDate = premiumReportUpdateReq.invoiceDueTimestamp
		? getInvoiceDueDate(premiumReportUpdateReq.invoiceDueTimestamp)
		: todaysDateWithTZ;

	const [reportEnd, setReportEnd] = useState<Date>(reportEndDate);
	const [invoiceDate, setInvoiceDate] = useState<Date>(reportInvoiceDate);
	const reportEndDateError = getReportEndDateError(
		reportEnd,
		reportStartDate,
		policy,
		authData,
	) as ReportDateError;

	useEffect(() => {
		const populateForm = () => {
			if (fields) {
				const newDefaultValues = getDefaultPolicyValues(fields, authData);
				reset(newDefaultValues);
			}
		};
		populateForm();
	}, [fields, reset, authData]);

	useEffect(() => {
		const fetchPRConfig = async (version?: string) => {
			try {
				setLoading(true);
				let products = [] as ProductConfigurationResponse[];
				if (version) {
					const productsPromise = await ProductApi.getProductsWithVersion(
						authData.auth.authorizationToken,
						version,
					);
					products = productsPromise.data;
				} else {
					const productsPromise = await ProductApi.getProducts(
						authData.auth.authorizationToken,
					);
					products = productsPromise.data;
				}
				const policyConfig = products.find(
					(p) => p.name === policy.productName,
				);
				const prConfig = policyConfig.premiumReportingConfiguration;
				const newFieldsConfig = prConfig.find(
					(c) => c.reportName === policy.premiumReportName,
				).fields;

				// update fields with reset field values
				await updateFields(newFieldsConfig, true);
				setLoading(false);
			} catch (err) {
				errorToast(err);
				setLoading(false);
			}
		};

		if (replaceMode) {
			fetchPRConfig();
			setInvoiceDate(getTodaysDateWithTimezone(authData.auth.timezone));
		} else {
			setPremiumReportUpdateReq(
				getUpdateReqFromPremiumReportResponse(premiumReport),
			);
			updateFields(premiumReportingConfiguration.fields);
		}
		setReportEnd(new Date(parseInt(premiumReport.endTimestamp, 10)));
		setInvoiceDate(
			getInvoiceDueDate(premiumReportUpdateReq.invoiceDueTimestamp),
		);
	}, [replaceMode, premiumReport, premiumReports]);

	useEffect(() => {
		const fetchVersion = async () => {
			try {
				setLoading(true);
				const products = await ProductApi.getProductsWithVersion(
					authData.auth.authorizationToken,
					premiumReport.configVersion,
				);
				const policyConfig = products.data.find(
					(p) => p.name === policy.productName,
				);
				const prConfig = policyConfig.premiumReportingConfiguration.find(
					(c) => c.reportName === policy.premiumReportName,
				);
				await updateFields(prConfig.fields);
				setLoading(false);
			} catch (err) {
				console.error(err);
				setLoading(false);
			}
		};
		const premiumReportVersion = premiumReport.configVersion;
		const policyConfigVersion = policy.configVersion;
		if (premiumReportVersion !== policyConfigVersion) {
			fetchVersion();
		}
		return () => {
			setReplaceMode(false); // clean up function to disable replaceMode whenever component unmounts
		};
	}, []);

	const updateEndTimestamp = (date: Date) => {
		const validDate = date && !isNaN(date.getTime());
		setReportEnd(date);
		premiumReportUpdateReq.endTimestamp = validDate
			? `${date.getTime()}`
			: null;
		setPremiumReportUpdateReq(premiumReportUpdateReq);
	};

	const updateInvoiceDueTimestamp = (date: Date) => {
		const validDate = date && !isNaN(date.getTime());
		setInvoiceDate(date);
		premiumReportUpdateReq.invoiceDueTimestamp = validDate
			? `${date.getTime()}`
			: null;
		setPremiumReportUpdateReq(premiumReportUpdateReq);
	};

	const hasUpdatePermission = hasPermission('premiumReport.update');
	const isEditable = state === 'draft';
	const isDisabled = hasUpdatePermission ? !isEditable && !replaceMode : true;
	const endDateDisabled = hasUpdatePermission
		? !isEditable || replaceMode
		: true;
	const renderDateFields = () => {
		return (
			<>
				<Grid item xs={3}>
					<DefaultDatePicker
						id='start-timestamp'
						label='Start Date'
						style={{ marginRight: '10px' }}
						disabled
						showCalendar
						value={reportStartDate}
						onChange={null}
					/>
				</Grid>
				<Grid item xs={3}>
					<DefaultDatePicker
						id='end-timestamp'
						minDate={reportStartDate}
						label='End Date'
						disabled={endDateDisabled}
						showCalendar
						required
						error={isEditable && !reportEndDateError.isValid}
						errorMessage={
							isEditable && !reportEndDateError.isValid
								? reportEndDateError.message
								: null
						}
						style={{ marginRight: '10px' }}
						value={reportEnd}
						onChange={updateEndTimestamp}
					/>
				</Grid>
				<Grid item xs={3}>
					{/* No validation needed on the invoiceDueTimestamp */}
					<DefaultDatePicker
						id='invoicedue'
						label='Invoice Due Date'
						showCalendar
						disabled={isDisabled}
						value={invoiceDate}
						onChange={updateInvoiceDueTimestamp}
					/>
				</Grid>
			</>
		);
	};

	const renderForm = () => {
		const formValues = getValues();

		return !premiumReportData.fields || isEmpty(formValues) ? (
			<LargeCenterSpinner />
		) : (
			<FastForm
				formName={FORM_NAME.PREMIUM_REPORT_FORM}
				form={fields}
				configVersion={productConfig.configVersion}
				authData={authData}
				productName={productConfig.name}
				disabled={isDisabled}
				setLoading={setLoading}
				save={premiumReportData.saveFastForm}
				disableExternalCalls
			/>
		);
	};

	const pageTitle = replaceMode
		? `Replacement for Premium Report ${premiumReport.displayId}`
		: 'Premium Report';

	return (
		<StyledSectionContainer>
			<StyledTitleContainer>
				<StyledTitle>{pageTitle}</StyledTitle>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink component={RouterLink} to='../auxData'>
						Aux Data
					</MaterialLink>
				)}
			</StyledTitleContainer>
			<hr />
			<Grid container spacing={2}>
				{renderDateFields()}
				{renderForm()}
			</Grid>
		</StyledSectionContainer>
	);
};

export default withPremiumReportContext<Props>(ReportDetails);
